import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../../services/api";
import { BackButton } from "../../../../components/button/back-button";
import { LoadingButton } from "../../../../components/button/loading-button";
import {
  AiFunctionsField,
  normalizeAiFunctionOptions,
  type AiFunctionOption,
} from "../../../../components/ai-functions-field";
import { useToast } from "../../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../../utils";
import type { AiFunction } from "../../../../types/tenant";
import type { AiSkill, AiSkillPayload } from "../../../../types/ai-skill";
import type { AiIntent } from "../../../../types/ai-intent";
import { AiIntentsField } from "../../../../components/ai-intents-field";

type SkillFormState = {
  name: string;
  slug: string;
  relatedIntents: string[];
  functions: AiFunction[];
  instruction: string;
  enabled: boolean;
};

const emptyForm: SkillFormState = {
  name: "",
  slug: "",
  relatedIntents: [],
  functions: [],
  instruction: "",
  enabled: true,
};

const toSlug = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const normalizeRelatedIntents = (items: string[]): string[] => {
  const seen = new Set<string>();
  const normalizedItems: string[] = [];

  items
    .map((item) => item.trim())
    .filter(Boolean)
    .forEach((item) => {
      if (seen.has(item)) return;
      seen.add(item);
      normalizedItems.push(item);
    });

  return normalizedItems;
};

const createFormFromSkill = (skill: AiSkill): SkillFormState => ({
  name: skill.name,
  slug: skill.slug,
  relatedIntents: normalizeRelatedIntents(
    (skill.relatedIntents ?? []).map((intent) => intent.slug),
  ),
  functions: skill.functions ?? [],
  instruction: skill.instruction,
  enabled: skill.enabled,
});

const buildPayload = (form: SkillFormState): AiSkillPayload => ({
  name: form.name.trim(),
  slug: form.slug.trim(),
  instruction: form.instruction.trim(),
  relatedIntents: normalizeRelatedIntents(form.relatedIntents),
  functions: form.functions,
  enabled: form.enabled,
});

export default function CadastroAiSkill() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error, warn } = useToast();

  const [form, setForm] = useState<SkillFormState>(emptyForm);
  const [functionOptions, setFunctionOptions] = useState<AiFunctionOption[]>(
    [],
  );
  const [intents, setIntents] = useState<AiIntent[]>([]);
  const [slugWasEdited, setSlugWasEdited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      try {
        setIsLoading(true);
        const functionRequest = api.get<unknown>("/tenant/ai-functions");
        const intentsRequest = api.get<AiIntent[]>("/ai-intents");

        if (id) {
          const [skillResponse, functionsResponse, intentsResponse] =
            await Promise.all([
              api.get<AiSkill>(`/ai-skills/${id}`),
              functionRequest,
              intentsRequest,
            ]);

          if (!cancelled) {
            setForm(createFormFromSkill(skillResponse.data));
            setSlugWasEdited(true);
            setFunctionOptions(
              normalizeAiFunctionOptions(functionsResponse.data),
            );
            setIntents(
              Array.isArray(intentsResponse.data) ? intentsResponse.data : [],
            );
          }
          return;
        }

        const [functionsResponse, intentsResponse] = await Promise.all([
          functionRequest,
          intentsRequest,
        ]);
        if (!cancelled) {
          setFunctionOptions(
            normalizeAiFunctionOptions(functionsResponse.data),
          );
          setIntents(
            Array.isArray(intentsResponse.data) ? intentsResponse.data : [],
          );
        }
      } catch (err) {
        if (!cancelled) {
          const apiMessage = getApiErrorMessage(err);
          error(apiMessage);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadData();

    return () => {
      cancelled = true;
    };
  }, [id, error]);

  const handleNameChange = (value: string) => {
    setForm((current) => ({
      ...current,
      name: value,
      slug: slugWasEdited ? current.slug : toSlug(value),
    }));
  };

  const handleSlugChange = (value: string) => {
    setSlugWasEdited(true);
    setForm((current) => ({ ...current, slug: toSlug(value) }));
  };

  const validateForm = () => {
    const payload = buildPayload(form);

    if (!payload.name) {
      warn("Informe o nome da skill.");
      return null;
    }

    if (!payload.slug) {
      warn("Informe o slug da skill.");
      return null;
    }

    if (!/^[a-z0-9_]+$/.test(payload.slug)) {
      warn("Use apenas letras minúsculas, números e underline no slug.");
      return null;
    }

    if (!payload.instruction) {
      warn("Informe as instruções da skill.");
      return null;
    }

    return payload;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;

    const payload = validateForm();
    if (!payload) return;

    try {
      setIsSubmitting(true);

      if (id) {
        await api.patch(`/ai-skills/${id}`, payload);
        success("Skill atualizada com sucesso.");
      } else {
        await api.post("/ai-skills", payload);
        success("Skill cadastrada com sucesso.");
      }

      navigate("/painel/config/ai-skills");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app-page">
      <BackButton />

      <h1 className="app-page-title mb-6">
        {id ? "Editar Skill de IA" : "Nova Skill de IA"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="form-label">Nome</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={(event) => handleNameChange(event.target.value)}
              className="form-input"
              disabled={isLoading || isSubmitting}
              required
            />
          </div>

          <div>
            <label className="form-label">Slug</label>
            <input
              type="text"
              name="slug"
              value={form.slug}
              onChange={(event) => handleSlugChange(event.target.value)}
              className="form-input"
              pattern="[a-z0-9_]+"
              title="Use apenas letras minúsculas, números e underline."
              disabled={isLoading || isSubmitting}
              required
            />
          </div>
        </div>

        <AiIntentsField
          intents={intents}
          value={form.relatedIntents}
          onChange={(relatedIntents) =>
            setForm((current) => ({ ...current, relatedIntents }))
          }
          isLoading={isLoading}
          isDisabled={isSubmitting}
        />

        <AiFunctionsField
          options={functionOptions}
          value={form.functions}
          onChange={(functions) =>
            setForm((current) => ({ ...current, functions }))
          }
          isLoading={isLoading}
          isDisabled={isSubmitting}
        />

        <div>
          <label className="form-label">Instruções</label>
          <textarea
            name="instruction"
            value={form.instruction}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                instruction: event.target.value,
              }))
            }
            className="form-input min-h-56 font-mono text-sm"
            disabled={isLoading || isSubmitting}
            required
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            name="enabled"
            checked={form.enabled}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                enabled: event.target.checked,
              }))
            }
            className="form-checkbox"
            disabled={isLoading || isSubmitting}
          />
          <label className="text-sm text-slate-700">Skill ativa</label>
        </div>

        <LoadingButton
          type="submit"
          className="btn btn-neutral cursor-pointer"
          isLoading={isSubmitting}
          loadingLabel={id ? "Salvando" : "Cadastrando"}
          disabled={isLoading}
        >
          {id ? "Salvar Alterações" : "Cadastrar Skill"}
        </LoadingButton>
      </form>
    </div>
  );
}
